function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Delta } from "../../components/analytics-kit";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import Chart from "../../components/chart";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { repSvc, safe, errText, runQs, metricLabel, fmtCell, fmtByKey, fmtMoney, kitKpiDelta, defaultWidth, rangeLabel } from "./common";
import { areaLabel, areaBlurb, groupLabel, findArea, buildTemplate } from "./templates";
import { pinPanels } from "../dashboard/layout";

/*
 * Report EDITOR (/Reports/new, /Reports/edit/:id) — 2026-10-09 rebuild to the
 * approved Editor + Add-a-panel boards (reportsflow boards 4 and 5).
 *
 * Composer top bar (name · live sub line · who can see it · Save and email
 * it · Save) · canvas of panels that render LIVE data (debounced auto
 * preview through POST /v1/reports/preview; no run button) · 340px side
 * panel editing the selected panel · "Add a panel" dialog.
 *
 * Rules kept from the first builder:
 *  - registry-driven: only what /v1/reports/registry returns is offered
 *    (money numbers are absent for managed workspaces, never greyed);
 *  - the saved definition shape is unchanged (kind kpi|line|bar|table,
 *    dataset, metrics, dimensions, filters, series for blended charts, top,
 *    sortBy, sortDir, width); editor-only fields (uid, _auto) never leave;
 *  - max 8 panels; blended charts need 2-4 series on date/market/product;
 *  - server error strings are shown verbatim.
 *
 * Preview cache: results are keyed by each panel's QUERY signature (area,
 * numbers, grouping, filters, top, sort, range, comparison; not the kind
 * or title), so switching Line/Bars/Table, renaming, reordering or resizing
 * re-renders instantly and only changed panels hit the server.
 *
 * One-chart mode (/Reports/new?chart=1, My Dashboard step 3): the Dashboard's "New chart". One panel, named in the top
 * bar; no sharing or email; "Add to Dashboard" saves it into the person's own "Dashboard charts" report
 * (POST /v1/reports/dashboard-chart), pins it and returns to the Dashboard.
 */

const MAX_PANELS = 8;
const KINDS = [["kpi", "Numbers"], ["line", "Line"], ["bar", "Bars"], ["table", "Table"]];
const BLEND_DIMS = ["date", "market", "product"];
const TOPS = [[5, "Top 5"], [10, "Top 10"], [25, "Top 25"], [0, "All"]];
const SERIES_COLORS = ["#ff6358", "#2f6fdb", "#2f9e6a", "#b25e09"];
const WIDTH_LABEL = {
  full: "Full width",
  half: "Half width",
  third: "Third width"
};
let UID = 0;
const nextUid = () => `rb${++UID}`;

// Global comparison tokens (prev_period / prev_year) → the report's own vocabulary.
function normComp(c) {
  const s = String(c || "none");
  if (s === "prev_period" || s === "previous_period") return "previous_period";
  if (s === "prev_year" || s === "previous_year") return "previous_year";
  return "none";
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function rangePhrase(token) {
  const now = new Date();
  if (token === "this_month") return `${MONTHS_LONG[now.getMonth()]} ${now.getFullYear()}`;
  if (token === "last_month") {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
  }
  if (token === "this_year") return `this year (${now.getFullYear()})`;
  if (token === "last_year") return String(now.getFullYear() - 1);
  if (/^custom_/.test(String(token || ""))) return rangeLabel(token);
  return rangeLabel(token).toLowerCase();
}
function fmtCat(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v == null ? "" : v));
  if (m) return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}`;
  return v == null || v === "" ? "(none)" : String(v);
}

// ------------------------------------------------------------------ panel helpers

function metricDef(registry, dataset, key) {
  const d = findArea(registry, dataset);
  return d && (d.metrics || []).find(m => m.key === key) || null;
}
function fmtVal(registry, dataset, key, v) {
  const md = metricDef(registry, dataset, key);
  if (md && md.format === "money") return fmtMoney(v);
  return fmtByKey(key, v);
}
function hasDim(ds, key) {
  return !!ds && (ds.dimensions || []).some(d => d.key === key);
}
function defaultDim(ds, kind) {
  const dims = (ds && ds.dimensions || []).map(d => d.key);
  if (!dims.length) return [];
  if (kind === "line") return [dims.includes("date") ? "date" : dims[0]];
  const other = dims.find(d => d !== "date");
  return [other || dims[0]];
}

// Plain auto-title, re-derived while the panel keeps its generated name.
function autoTitle(registry, p) {
  if (p.series && p.series.length) {
    const first = p.series[0];
    const dim = (p.dimensions || [])[0] || "date";
    return `${metricLabel(registry, first.dataset, first.metric)} and more by ${dim === "date" ? "day" : groupLabel(registry, first.dataset, dim).toLowerCase()}`;
  }
  const area = areaLabel(registry, p.dataset);
  if (p.kind === "kpi") return `${area} at a glance`;
  const m = (p.metrics || [])[0];
  const head = m ? metricLabel(registry, p.dataset, m) : area;
  const dim = (p.dimensions || [])[0];
  if (!dim) return head;
  return `${head} by ${dim === "date" ? "day" : groupLabel(registry, p.dataset, dim).toLowerCase()}`;
}
// Titles the first builder generated ("<Dataset label> · KPI strip", older " — ").
const OLD_KIND = {
  kpi: "KPI strip",
  line: "Line",
  bar: "Bar",
  table: "Table"
};
function isOldAutoTitle(registry, p) {
  const d = findArea(registry, p.dataset) || {};
  const t = `${d.label || "New"} · ${OLD_KIND[p.kind] || p.kind}`;
  return p.title === t || p.title === t.replace(" · ", " — ");
}
function validPanel(registry, p) {
  if (p.series && p.series.length) return p.series.length >= 2 && p.series.length <= 4 && p.series.every(s => s.dataset && s.metric);
  return !!findArea(registry, p.dataset) && (p.metrics || []).length > 0;
}

// What the server needs (the saved shape; editor-only fields dropped).
function cleanPanel(p, registry) {
  return {
    // The stable id keeps Dashboard pins on this panel through edits (My Dashboard spec); new panels get one on save.
    id: p.id || undefined,
    kind: p.kind,
    title: String(p.title || "").trim() || autoTitle(registry, p),
    dataset: p.series ? undefined : p.dataset,
    series: p.series || undefined,
    metrics: p.series ? undefined : p.metrics,
    dimensions: p.dimensions,
    filters: p.filters,
    top: p.top || undefined,
    sortBy: p.sortBy || undefined,
    sortDir: p.sortDir || undefined,
    width: p.width || undefined
  };
}
function unblend(n) {
  if (!n.series || !n.series.length) return n;
  const s = n.series[0];
  return {
    ...n,
    dataset: s.dataset,
    metrics: s.metric ? [s.metric] : [],
    series: null
  };
}

// Apply an edit to a panel, keeping it coherent (kind rules, area switch, auto-title).
function applyPatch(registry, p, q) {
  let n = {
    ...p,
    ...q
  };
  if ("dataset" in q && q.dataset !== p.dataset) {
    const ds = findArea(registry, q.dataset);
    if (!("metrics" in q)) n.metrics = (ds && ds.metrics || []).slice(0, n.kind === "kpi" ? 4 : 3).map(m => m.key);
    if (!("dimensions" in q)) {
      n.dimensions = n.kind === "kpi" ? [] : (p.dimensions || []).filter(d => hasDim(ds, d)).slice(0, 1);
      if (n.kind !== "kpi" && !n.dimensions.length) n.dimensions = defaultDim(ds, n.kind);
    }
    n.filters = {};
    n.sortBy = null;
  }
  if ("kind" in q && q.kind !== p.kind) {
    const ds = findArea(registry, n.dataset);
    if (n.kind === "kpi" || n.kind === "table") n = unblend(n);
    if (n.kind === "kpi") {
      n.dimensions = [];
      n.top = null;
      n.sortBy = null;
    } else if (!(n.dimensions || []).length) n.dimensions = n.series ? ["date"] : defaultDim(findArea(registry, n.dataset) || ds, n.kind);
    if (n.kind === "line") {
      n.top = null;
    }
    if (!p.width || p.width === defaultWidth(p.kind)) n.width = defaultWidth(n.kind);
  }
  if (n.sortBy && !(n.metrics || []).includes(n.sortBy)) n.sortBy = null;
  // A typed name sticks (even while the field is emptied to retype); otherwise the name follows the setup.
  if ("title" in q) n._auto = false;else if (n._auto) n.title = autoTitle(registry, n);
  return n;
}
function freshPanel(registry, dataset, kind = "kpi") {
  const ds = findArea(registry, dataset) || (registry.datasets || [])[0] || {};
  const p = {
    uid: nextUid(),
    _auto: true,
    kind,
    dataset: ds.key,
    metrics: (ds.metrics || []).slice(0, kind === "kpi" ? 4 : 3).map(m => m.key),
    dimensions: kind === "kpi" ? [] : defaultDim(ds, kind),
    filters: {},
    top: null,
    sortBy: null,
    sortDir: "desc",
    series: null,
    width: defaultWidth(kind)
  };
  return {
    ...p,
    title: autoTitle(registry, p)
  };
}

// ------------------------------------------------------------------ component

class ReportBuilderComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onDocDown", e => {
      // Outside click closes the ··· menu (ref containment; React's stopPropagation can't stop a document listener).
      if (this.state.menu != null && !(e.target.closest && e.target.closest(".rb-menu-wrap"))) this.setState({
        menu: null
      });
    });
    _defineProperty(this, "onKey", e => {
      if (e.key !== "Escape") return;
      if (this.state.adding) this.setState({
        adding: null
      });else if (this.state.menu != null) this.setState({
        menu: null
      });
    });
    _defineProperty(this, "retryPreview", () => {
      this.setState({
        previewNote: null
      });
      this.runPreview(this.state.panels);
    });
    _defineProperty(this, "back", () => {
      this.props.history.push(this.chartMode() ? "/Dashboard" : this.id() ? `/Reports/view/${this.id()}` : "/Reports");
    });
    _defineProperty(this, "addToDashboard", async () => {
      const {
        registry,
        panels,
        range,
        comparison
      } = this.state;
      const p = panels[0];
      if (!p) return;
      if (!validPanel(registry, p)) {
        this.setState({
          note: p.series ? "This chart needs numbers from at least two areas." : "Pick at least one number for this chart."
        });
        return;
      }
      this.setState({
        saveBusy: true,
        note: null
      });
      try {
        const r = await repSvc("/dashboard-chart").postAsync({
          panel: cleanPanel(p, registry),
          range,
          comparison
        });
        const pin = await pinPanels([{
          reportId: String(r.reportId),
          panelId: String(r.panelId),
          width: p.width || defaultWidth(p.kind)
        }]);
        const title = String(r.title || p.title || "Your chart");
        const msg = pin.full ? `Saved "${title}" in ${r.reportName}, but your Dashboard is full (24 cards). Remove a card, then add it from Reports.` : `Added "${title}" to your Dashboard. It's saved in your ${r.reportName} report.`;
        try {
          sessionStorage.setItem("plunk-dash-toast", msg);
        } catch (e) {/* no toast */}
        if (this._alive) this.props.history.push("/Dashboard");
      } catch (e) {
        if (this._alive) this.setState({
          saveBusy: false,
          note: errText(e, "The chart couldn't be saved. Try again.")
        });
      }
    });
    _defineProperty(this, "save", async (andEmail = false) => {
      const {
        registry,
        panels
      } = this.state;
      if (!this.state.name.trim()) {
        this.setState({
          note: "Give the report a name first."
        });
        if (this._nameRef) this._nameRef.focus();
        return;
      }
      if (!panels.length) {
        this.setState({
          note: "Add at least one panel first."
        });
        return;
      }
      const bad = panels.findIndex(p => !validPanel(registry, p));
      if (bad >= 0) {
        const p = panels[bad];
        this.setState({
          sel: bad,
          note: p.series ? `"${p.title || "This panel"}" needs numbers from at least two areas.` : `"${p.title || "This panel"}" needs at least one number.`
        });
        return;
      }
      this.setState({
        saveBusy: true,
        note: null
      });
      try {
        // "Save and email it" lands on the report with the email schedule open (?schedule=1).
        const qs = andEmail === true ? "?schedule=1" : "";
        if (this.id()) {
          await repSvc(`/${this.id()}`).putAsync(this.definition());
          if (this._alive) this.props.history.push(`/Reports/view/${this.id()}${qs}`);
        } else {
          const created = await repSvc("").postAsync(this.definition());
          if (this._alive) this.props.history.push(`/Reports/view/${String(created.id || (created.report || {}).id)}${qs}`);
        }
      } catch (e) {
        // Validation 400s carry a plain error string: shown verbatim.
        if (this._alive) this.setState({
          saveBusy: false,
          note: errText(e, "The report couldn't be saved. Try again.")
        });
      }
    });
    _defineProperty(this, "openAdd", () => {
      const {
        registry,
        panels
      } = this.state;
      if (panels.length >= MAX_PANELS) return;
      const first = (registry.datasets || [])[0];
      if (!first) return;
      this.setState({
        adding: {
          draft: freshPanel(registry, first.key, "kpi")
        },
        menu: null
      });
    });
    // ---------------------------------------------------------------- add a panel dialog
    _defineProperty(this, "patchDraft", q => {
      const {
        registry,
        adding
      } = this.state;
      if (!adding) return;
      this.setState({
        adding: {
          ...adding,
          draft: applyPatch(registry, adding.draft, q)
        }
      });
    });
    _defineProperty(this, "commitAdd", () => {
      const {
        adding,
        panels,
        registry
      } = this.state;
      if (!adding || panels.length >= MAX_PANELS || !validPanel(registry, adding.draft)) return;
      this.setState({
        panels: [...panels, {
          ...adding.draft,
          uid: nextUid()
        }],
        sel: panels.length,
        adding: null
      });
    });
    this.state = {
      registry: null,
      error: false,
      note: null,
      previewNote: null,
      // New reports start unnamed (the placeholder shows); Save refuses and focuses the field.
      name: "",
      description: "",
      range: getGlobalRange(),
      comparison: normComp(getGlobalComp()),
      shared: true,
      panels: [],
      sel: 0,
      menu: null,
      adding: null,
      previewBusy: false,
      saveBusy: false,
      tick: 0
    };
    this._alive = true;
    this._cache = {}; // query signature -> panel result
    this._inflight = new Set();
    this._shown = {}; // uid -> last result drawn (kept while a change loads)
  }
  id() {
    return this.props.params && this.props.params.id ? String(this.props.params.id) : null;
  }
  chartMode() {
    if (this.id()) return false;
    try {
      return new URLSearchParams(this.props.location && this.props.location.search || window.location.search).get("chart") === "1";
    } catch (e) {
      return false;
    }
  }
  async componentDidMount() {
    document.addEventListener("mousedown", this.onDocDown);
    document.addEventListener("keydown", this.onKey);
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    // The saved range follows the shared top-bar period control.
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      this.setState({
        range
      });
    });
    const registry = await safe(repSvc("/registry").getAsync(), null);
    if (!this._alive) return;
    if (!registry) {
      this.setState({
        error: true
      });
      return;
    }
    if (this.id()) {
      const def = await safe(repSvc(`/${this.id()}`).getAsync(), null);
      if (!this._alive) return;
      if (!def) {
        this.setState({
          error: true
        });
        return;
      }
      const panels = (def.panels || []).map(p => {
        const q = {
          ...p,
          uid: nextUid(),
          filters: p.filters || {},
          series: p.series && p.series.length ? p.series : null,
          metrics: p.metrics || [],
          dimensions: p.dimensions || []
        };
        q._auto = !q.title || isOldAutoTitle(registry, q) || q.title === autoTitle(registry, q);
        if (q._auto) q.title = autoTitle(registry, q);
        return q;
      });
      this.setState({
        registry,
        panels,
        name: def.name || "",
        description: def.description || "",
        comparison: def.comparison ? normComp(def.comparison) : this.state.comparison,
        shared: !(def.shared === false || def.shared === 0)
      });
      return;
    }
    if (this.chartMode()) {
      // The comparison follows the global picker like everywhere else (the report-wide switch is hidden here).
      // Start on one chart with its editor open: a line of the first area, since a Dashboard chart is usually a trend.
      const first = (registry.datasets || [])[0];
      this.setState({
        registry,
        panels: first ? [freshPanel(registry, first.key, "line")] : [],
        sel: 0,
        comparison: normComp(getGlobalComp())
      });
      return;
    }
    let q = null;
    try {
      q = new URLSearchParams(this.props.location && this.props.location.search || window.location.search);
    } catch (e) {
      q = null;
    }
    const tplKey = q && q.get("template");
    const tpl = tplKey ? buildTemplate(registry, tplKey) : null;
    if (tpl) {
      const panels = tpl.panels.map(p => ({
        ...p,
        uid: nextUid(),
        _auto: false
      }));
      this.setState({
        registry,
        panels,
        name: tpl.name || "",
        comparison: tpl.comparison || this.state.comparison
      });
      return;
    }
    // Prefilled start (the Sales cockpit's "Open as report"): ?dataset=<key or label>&filter.<dim>=<value>
    // picks the area by key, else by a label match, and seeds one table panel with the filters set.
    const want = String(q && q.get("dataset") || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (want) {
      const ds = (registry.datasets || []).find(d => String(d.key || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim() === want) || (registry.datasets || []).find(d => String(d.label || "").toLowerCase().includes(want.split(" ")[0]));
      if (ds) {
        const filters = {};
        q.forEach((v, k) => {
          if (k.startsWith("filter.") && v) filters[k.slice(7)] = v;
        });
        const p = freshPanel(registry, ds.key, "table");
        p.metrics = (ds.metrics || []).slice(0, 5).map(m => m.key);
        p.dimensions = (ds.dimensions || []).slice(0, 1).map(d => d.key);
        p.filters = filters;
        p.title = autoTitle(registry, p);
        this.setState({
          registry,
          panels: [p]
        });
        return;
      }
    }
    // Blank: the canvas shows one "Add a panel" prompt.
    this.setState({
      registry,
      panels: []
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
    clearTimeout(this._pvTimer);
    clearTimeout(this._addTimer);
    document.removeEventListener("mousedown", this.onDocDown);
    document.removeEventListener("keydown", this.onKey);
  }
  componentDidUpdate(prevProps, prevState) {
    const s = this.state;
    if (s.registry && (prevState.panels !== s.panels || prevState.range !== s.range || prevState.comparison !== s.comparison || prevState.registry !== s.registry)) {
      this.schedulePreview();
    }
    if (s.adding && prevState.adding !== s.adding) {
      const prevDraft = prevState.adding && prevState.adding.draft;
      if (prevDraft !== s.adding.draft || prevState.range !== s.range) this.scheduleAddPreview();
    }
  }
  // ---------------------------------------------------------------- live preview

  sig(p) {
    const {
      range,
      comparison
    } = this.state;
    return JSON.stringify([p.series ? null : p.dataset, p.series || null, p.series ? null : p.metrics, p.dimensions, p.filters || {}, p.top || null, p.sortBy || null, p.sortDir || null, range, comparison]);
  }
  resultFor(p) {
    const r = this._cache[this.sig(p)];
    if (r) {
      this._shown[p.uid] = r;
      return {
        r,
        stale: false
      };
    }
    const old = this._shown[p.uid];
    return {
      r: old || null,
      stale: !!old
    };
  }
  schedulePreview() {
    clearTimeout(this._pvTimer);
    this._pvTimer = setTimeout(() => this.runPreview(this.state.panels), 450);
  }
  scheduleAddPreview() {
    clearTimeout(this._addTimer);
    this._addTimer = setTimeout(() => {
      if (this.state.adding) this.runPreview([this.state.adding.draft]);
    }, 450);
  }
  // Runs only the valid panels whose query isn't cached yet, in one POST (≤ 8 by construction).
  async runPreview(list) {
    const {
      registry,
      range,
      comparison,
      name
    } = this.state;
    if (!registry) return;
    const need = [];
    const seen = new Set();
    (list || []).forEach(p => {
      if (!p || !validPanel(registry, p)) return;
      const sig = this.sig(p);
      if (this._cache[sig] || this._inflight.has(sig) || seen.has(sig)) return;
      seen.add(sig);
      need.push({
        sig,
        p
      });
    });
    if (!need.length) return;
    const batch = need.slice(0, MAX_PANELS);
    batch.forEach(n => this._inflight.add(n.sig));
    this.setState({
      previewBusy: true
    });
    try {
      const def = {
        name: name.trim() || "Preview",
        range,
        comparison,
        shared: true,
        panels: batch.map(n => cleanPanel(n.p, registry))
      };
      const run = await repSvc(`/preview?${runQs(range)}`).postAsync(def);
      (run && run.panels || []).forEach((r, i) => {
        if (batch[i]) this._cache[batch[i].sig] = r;
      });
      batch.forEach(n => this._inflight.delete(n.sig));
      if (this._alive) this.setState(s => ({
        previewBusy: this._inflight.size > 0,
        previewNote: null,
        tick: s.tick + 1
      }));
    } catch (e) {
      batch.forEach(n => this._inflight.delete(n.sig));
      if (this._alive) this.setState(s => ({
        previewBusy: this._inflight.size > 0,
        previewNote: errText(e, "We couldn't load the numbers for this report just now."),
        tick: s.tick + 1
      }));
    }
  }

  // ---------------------------------------------------------------- edits

  definition() {
    const {
      name,
      description,
      range,
      comparison,
      shared,
      panels,
      registry
    } = this.state;
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      range,
      comparison,
      shared,
      panels: panels.map(p => cleanPanel(p, registry))
    };
  }
  updatePanel(i, q) {
    const {
      registry
    } = this.state;
    this.setState({
      panels: this.state.panels.map((p, j) => j === i ? applyPatch(registry, p, q) : p)
    });
  }
  movePanel(i, dir) {
    const {
      panels,
      sel
    } = this.state;
    const j = i + dir;
    if (j < 0 || j >= panels.length) return;
    const next = panels.slice();
    [next[i], next[j]] = [next[j], next[i]];
    this.setState({
      panels: next,
      sel: sel === i ? j : sel === j ? i : sel,
      menu: null
    });
  }
  duplicatePanel(i) {
    const {
      panels
    } = this.state;
    if (panels.length >= MAX_PANELS) return;
    const src = panels[i];
    const copy = {
      ...src,
      id: undefined,
      uid: nextUid(),
      filters: {
        ...(src.filters || {})
      },
      series: src.series ? src.series.map(s => ({
        ...s
      })) : null
    };
    const next = panels.slice();
    next.splice(i + 1, 0, copy);
    this.setState({
      panels: next,
      sel: i + 1,
      menu: null
    });
  }
  removePanel(i) {
    const next = this.state.panels.filter((_, j) => j !== i);
    this.setState({
      panels: next,
      sel: Math.max(0, Math.min(this.state.sel, next.length - 1)),
      menu: null
    });
  }
  toggleWidth(i) {
    const p = this.state.panels[i];
    const w = p.width || defaultWidth(p.kind);
    this.updatePanel(i, {
      width: w === "full" ? "half" : "full"
    });
    this.setState({
      menu: null
    });
  }
  // ---------------------------------------------------------------- panel body (live data)

  renderBody(p) {
    const {
      registry,
      comparison
    } = this.state;
    if (!validPanel(registry, p)) {
      return /*#__PURE__*/React.createElement("p", {
        className: "rb-hint"
      }, p.series ? "Pick numbers from at least two areas to draw this chart." : "Pick at least one number to show here.");
    }
    const {
      r,
      stale
    } = this.resultFor(p);
    if (!r) return /*#__PURE__*/React.createElement("div", {
      className: "rb-loading"
    }, /*#__PURE__*/React.createElement(ChartLoader, null));
    if (r.error) return /*#__PURE__*/React.createElement("div", {
      className: "wa-notice rb-notice"
    }, r.error);
    const keys = r.columns || [];
    const rows = r.rows || [];
    const blended = !!(p.series && p.series.length);
    // Which column is which: blended = [dim, ...series]; else dims first, then numbers.
    const dimCount = blended ? 1 : keys.filter(k => !metricDef(registry, p.dataset, k)).length;
    const colInfo = keys.map((k, ci) => {
      if (blended) {
        if (ci === 0) return {
          key: k,
          label: groupLabel(registry, p.series[0].dataset, k),
          dim: true
        };
        const s = p.series[ci - 1] || {};
        return {
          key: s.metric,
          ds: s.dataset,
          label: s.label || `${metricLabel(registry, s.dataset, s.metric)} (${areaLabel(registry, s.dataset)})`
        };
      }
      const isDim = ci < dimCount;
      return {
        key: k,
        ds: p.dataset,
        label: isDim ? groupLabel(registry, p.dataset, k) : metricLabel(registry, p.dataset, k),
        dim: isDim
      };
    });
    const cls = `rb-body${stale ? " rb-stale" : ""}`;
    if (!rows.length) return /*#__PURE__*/React.createElement("div", {
      className: cls
    }, /*#__PURE__*/React.createElement("p", {
      className: "rb-hint"
    }, "Nothing to show for this period yet."));
    if (p.kind === "kpi") {
      const now = rows[0] || [];
      const prev = comparison !== "none" ? (r.previousRows || [])[0] || null : null;
      const tiles = colInfo.map((c, ci) => ({
        c,
        ci
      })).filter(x => !x.c.dim);
      return /*#__PURE__*/React.createElement("div", {
        className: cls
      }, /*#__PURE__*/React.createElement("div", {
        className: "rb-kpis",
        style: {
          gridTemplateColumns: `repeat(${Math.min(tiles.length, 6)}, minmax(0, 1fr))`
        }
      }, tiles.map(({
        c,
        ci
      }) => /*#__PURE__*/React.createElement("div", {
        className: "rb-kpi",
        key: ci
      }, /*#__PURE__*/React.createElement("span", {
        className: "l"
      }, c.label), /*#__PURE__*/React.createElement("b", {
        className: "v"
      }, fmtVal(registry, c.ds, c.key, now[ci])), comparison !== "none" && kitKpiDelta(now[ci], prev, ci) ? /*#__PURE__*/React.createElement("span", {
        className: "d"
      }, /*#__PURE__*/React.createElement(Delta, {
        d: kitKpiDelta(now[ci], prev, ci)
      })) : null))));
    }
    if (p.kind === "line" || p.kind === "bar") {
      const valueCols = colInfo.map((c, ci) => ({
        c,
        ci
      })).filter(x => !x.c.dim);
      const data = [[colInfo[0].label, ...valueCols.map(x => x.c.label)]];
      // Today's partial day reads as a cliff; leave it out like the report page does.
      const todayKey = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      })();
      const chartRows = rows.filter(row => String(row[0]).slice(0, 10) !== todayKey);
      chartRows.forEach(row => data.push([fmtCat(row[0]), ...valueCols.map(x => row[x.ci] == null ? null : Number(row[x.ci]) || 0)]));
      const series = {};
      valueCols.forEach((x, si) => {
        series[si] = {
          color: SERIES_COLORS[si % SERIES_COLORS.length]
        };
        const md = metricDef(registry, x.c.ds, x.c.key);
        if (md && md.format === "money") series[si].tooltipFormat = "currency";
      });
      const step = Math.max(1, Math.ceil(rows.length / 8));
      return /*#__PURE__*/React.createElement("div", {
        className: cls
      }, /*#__PURE__*/React.createElement(Chart, {
        width: "100%",
        height: "200px",
        chartType: p.kind === "line" ? "LineChart" : "ColumnChart",
        loader: /*#__PURE__*/React.createElement(ChartLoader, null),
        data: data,
        options: {
          legend: {
            position: valueCols.length > 1 ? "bottom" : "none"
          },
          series,
          pointSize: rows.length > 40 ? 0 : 4,
          vAxis: {
            format: "short"
          },
          hAxis: {
            showTextEvery: step,
            slantedText: false
          }
        }
      }));
    }

    // table: the panel's top N (10 when unset), formatted like the report page
    const limit = p.top || 10;
    return /*#__PURE__*/React.createElement("div", {
      className: cls
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table rb-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, colInfo.map((c, ci) => /*#__PURE__*/React.createElement("th", {
      key: ci,
      className: c.dim ? "" : "n"
    }, c.label)))), /*#__PURE__*/React.createElement("tbody", null, rows.slice(0, limit).map((row, ri) => /*#__PURE__*/React.createElement("tr", {
      key: ri
    }, row.map((v, ci) => /*#__PURE__*/React.createElement("td", {
      key: ci,
      className: colInfo[ci] && colInfo[ci].dim ? "" : "n"
    }, colInfo[ci] && colInfo[ci].dim ? colInfo[ci].key === "date" ? fmtCat(v) : fmtCell(v) : fmtVal(registry, colInfo[ci] && colInfo[ci].ds, colInfo[ci] && colInfo[ci].key, v)))))))), rows.length > limit ? /*#__PURE__*/React.createElement("p", {
      className: "rb-foot"
    }, "Showing ", limit, " of ", rows.length, " rows here. The report page shows up to 100.") : null);
  }

  // ---------------------------------------------------------------- canvas

  renderCard(p, i) {
    const {
      sel,
      menu,
      panels
    } = this.state;
    const w = p.width || defaultWidth(p.kind);
    const open = menu === p.uid;
    return /*#__PURE__*/React.createElement("section", {
      key: p.uid,
      className: `rb-card w-${w}${i === sel ? " on" : ""}`,
      onClick: () => this.setState({
        sel: i
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-card-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rb-card-title"
    }, p.title || autoTitle(this.state.registry, p)), /*#__PURE__*/React.createElement("span", {
      className: "rb-card-width"
    }, WIDTH_LABEL[w] || "Full width"), !this.chartMode() && /*#__PURE__*/React.createElement("span", {
      className: "rb-menu-wrap",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "rb-dots",
      "aria-label": "Panel options",
      "aria-expanded": open,
      onClick: () => this.setState({
        menu: open ? null : p.uid,
        sel: i
      })
    }, "\xB7\xB7\xB7"), open ? /*#__PURE__*/React.createElement("div", {
      className: "rb-menu",
      role: "menu"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: i === 0,
      onClick: () => this.movePanel(i, -1)
    }, "Move up"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: i === panels.length - 1,
      onClick: () => this.movePanel(i, 1)
    }, "Move down"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      disabled: panels.length >= MAX_PANELS,
      onClick: () => this.duplicatePanel(i)
    }, "Duplicate"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => this.toggleWidth(i)
    }, w === "full" ? "Half width" : "Full width"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "danger",
      onClick: () => this.removePanel(i)
    }, "Remove")) : null)), this.renderBody(p));
  }
  // ---------------------------------------------------------------- shared controls (side panel + dialog)

  kindSeg(p, patch) {
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-seg four",
      role: "group",
      "aria-label": "Show as"
    }, KINDS.map(([k, label]) => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: k,
      className: p.kind === k ? "on" : "",
      onClick: () => patch({
        kind: k
      })
    }, label)));
  }
  numberChecks(p, patch) {
    const {
      registry
    } = this.state;
    const ds = findArea(registry, p.dataset) || {};
    const toggle = key => {
      const has = p.metrics.includes(key);
      // keep registry order so the panel reads the same way the list does
      const next = (ds.metrics || []).map(m => m.key).filter(k => k === key ? !has : p.metrics.includes(k));
      patch({
        metrics: next
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-checks"
    }, (ds.metrics || []).map(m => /*#__PURE__*/React.createElement("label", {
      key: m.key,
      className: "rb-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: p.metrics.includes(m.key),
      onChange: () => toggle(m.key)
    }), /*#__PURE__*/React.createElement("span", null, m.label))));
  }
  groupBy(p, patch) {
    const {
      registry
    } = this.state;
    if (p.kind === "kpi") return null;
    const blended = !!(p.series && p.series.length);
    let opts;
    if (blended) {
      opts = BLEND_DIMS.filter(d => p.series.every(s => hasDim(findArea(registry, s.dataset), d))).map(d => ({
        key: d,
        label: groupLabel(registry, p.series[0].dataset, d)
      }));
    } else {
      opts = ((findArea(registry, p.dataset) || {}).dimensions || []).map(d => ({
        key: d.key,
        label: groupLabel(registry, p.dataset, d.key)
      }));
    }
    if (!opts.length) return null;
    const cur = (p.dimensions || [])[0] || "";
    return /*#__PURE__*/React.createElement("label", {
      className: "rb-field"
    }, "Group by", /*#__PURE__*/React.createElement("select", {
      className: "rb-select",
      value: cur,
      onChange: e => patch({
        dimensions: e.target.value ? [e.target.value] : []
      })
    }, p.kind === "table" && !blended ? /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Nothing, just the totals") : null, opts.map(o => /*#__PURE__*/React.createElement("option", {
      key: o.key,
      value: o.key
    }, o.label))), (p.dimensions || []).length > 1 ? /*#__PURE__*/React.createElement("span", {
      className: "rb-sub"
    }, "Also grouped by ", p.dimensions.slice(1).map(d => groupLabel(registry, p.dataset, d).toLowerCase()).join(", "), ". Picking here keeps just one.") : null);
  }
  showTop(p, patch) {
    const {
      registry
    } = this.state;
    if (!(p.kind === "table" || p.kind === "bar") || p.series && p.series.length) return null;
    const dim = (p.dimensions || [])[0];
    if (!dim || dim === "date") return null;
    const sortKey = p.sortBy || p.metrics[0] || "";
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-field"
    }, "Show", /*#__PURE__*/React.createElement("div", {
      className: "rb-row"
    }, /*#__PURE__*/React.createElement("select", {
      className: "rb-select",
      "aria-label": "How many rows",
      value: p.top || 0,
      onChange: e => patch({
        top: Number(e.target.value) || null
      })
    }, TOPS.map(([n, l]) => /*#__PURE__*/React.createElement("option", {
      key: n,
      value: n
    }, l))), /*#__PURE__*/React.createElement("select", {
      className: "rb-select",
      "aria-label": "Ranked by",
      value: sortKey,
      onChange: e => patch({
        sortBy: e.target.value || null,
        sortDir: "desc"
      })
    }, p.metrics.map(m => /*#__PURE__*/React.createElement("option", {
      key: m,
      value: m
    }, "by ", metricLabel(registry, p.dataset, m).toLowerCase())))));
  }

  // Blended charts: numbers from up to four areas on one shared day/market/product axis.
  blendEditor(p, patch) {
    const {
      registry
    } = this.state;
    const datasets = registry.datasets || [];
    const dim = (p.dimensions || [])[0] || "date";
    const fits = datasets.filter(d => hasDim(d, dim));
    const setSeries = series => patch({
      series
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-blend"
    }, p.series.map((s, si) => {
      const sds = findArea(registry, s.dataset) || {};
      return /*#__PURE__*/React.createElement("div", {
        className: "rb-blend-row",
        key: si
      }, /*#__PURE__*/React.createElement("span", {
        className: "rb-swatch",
        style: {
          background: SERIES_COLORS[si % SERIES_COLORS.length]
        }
      }), /*#__PURE__*/React.createElement("select", {
        className: "rb-select",
        "aria-label": "Area",
        value: s.dataset,
        onChange: e => {
          const nd = findArea(registry, e.target.value) || {};
          setSeries(p.series.map((x, sj) => sj === si ? {
            ...x,
            dataset: e.target.value,
            metric: ((nd.metrics || [])[0] || {}).key || ""
          } : x));
        }
      }, fits.map(d => /*#__PURE__*/React.createElement("option", {
        key: d.key,
        value: d.key
      }, areaLabel(registry, d.key)))), /*#__PURE__*/React.createElement("select", {
        className: "rb-select",
        "aria-label": "Number",
        value: s.metric,
        onChange: e => setSeries(p.series.map((x, sj) => sj === si ? {
          ...x,
          metric: e.target.value
        } : x))
      }, (sds.metrics || []).map(m => /*#__PURE__*/React.createElement("option", {
        key: m.key,
        value: m.key
      }, m.label))), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "rb-x",
        "aria-label": "Remove these numbers",
        onClick: () => {
          const rest = p.series.filter((_, sj) => sj !== si);
          if (rest.length <= 1) {
            const keep = rest[0] || p.series[0];
            patch({
              series: null,
              dataset: keep.dataset,
              metrics: [keep.metric]
            });
          } else setSeries(rest);
        }
      }, "\u2715"));
    }), p.series.length < 4 ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link rb-link",
      onClick: () => {
        const used = new Set(p.series.map(s => s.dataset));
        const d = fits.find(x => !used.has(x.key)) || fits[0];
        if (!d) return;
        setSeries([...p.series, {
          dataset: d.key,
          metric: ((d.metrics || [])[0] || {}).key || ""
        }]);
      }
    }, "Add numbers from another area") : /*#__PURE__*/React.createElement("span", {
      className: "rb-sub"
    }, "A chart can show numbers from up to four areas."));
  }
  startBlend(p, patch) {
    const {
      registry
    } = this.state;
    const datasets = registry.datasets || [];
    const ds = findArea(registry, p.dataset);
    const cur = (p.dimensions || [])[0];
    const dim = BLEND_DIMS.includes(cur) && hasDim(ds, cur) ? cur : "date";
    if (!hasDim(ds, dim)) return null;
    const other = datasets.find(d => d.key !== p.dataset && hasDim(d, dim));
    if (!other) return null;
    return () => patch({
      series: [{
        dataset: p.dataset,
        metric: p.metrics[0] || ((ds.metrics || [])[0] || {}).key || ""
      }, {
        dataset: other.key,
        metric: ((other.metrics || [])[0] || {}).key || ""
      }],
      dimensions: [dim],
      top: null,
      sortBy: null
    });
  }
  numbersSection(p, patch, {
    allowBlend
  }) {
    const {
      registry
    } = this.state;
    const blended = !!(p.series && p.series.length);
    if (blended) {
      return /*#__PURE__*/React.createElement("div", {
        className: "rb-field"
      }, "Numbers from more than one area", this.blendEditor(p, patch));
    }
    const canBlend = allowBlend && (p.kind === "line" || p.kind === "bar") ? this.startBlend(p, patch) : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "rb-from"
    }, "Numbers from", /*#__PURE__*/React.createElement("select", {
      className: "rb-select rb-inline",
      "aria-label": "Area",
      value: p.dataset,
      onChange: e => patch({
        dataset: e.target.value
      })
    }, (registry.datasets || []).map(d => /*#__PURE__*/React.createElement("option", {
      key: d.key,
      value: d.key
    }, areaLabel(registry, d.key))))), this.numberChecks(p, patch), canBlend ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link rb-link",
      onClick: canBlend
    }, "Add numbers from another area") : null);
  }
  filterChips(p, patch) {
    const {
      registry
    } = this.state;
    const f = p.filters || {};
    const keys = Object.keys(f).filter(k => f[k]);
    if (!keys.length || p.series && p.series.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-field"
    }, "Showing only", /*#__PURE__*/React.createElement("div", {
      className: "rb-chips"
    }, keys.map(k => /*#__PURE__*/React.createElement("span", {
      className: "rb-chip",
      key: k
    }, groupLabel(registry, p.dataset, k), ": ", String(f[k]), /*#__PURE__*/React.createElement("button", {
      type: "button",
      "aria-label": "Show everything",
      onClick: () => {
        const nf = {
          ...f
        };
        delete nf[k];
        patch({
          filters: nf
        });
      }
    }, "\u2715")))));
  }

  // ---------------------------------------------------------------- side panel

  renderSide() {
    const {
      panels,
      sel,
      comparison
    } = this.state;
    const p = panels[sel];
    if (!p) {
      return /*#__PURE__*/React.createElement("aside", {
        className: "rb-side"
      }, /*#__PURE__*/React.createElement("div", {
        className: "rb-side-head"
      }, /*#__PURE__*/React.createElement("b", null, "Start with a panel"), /*#__PURE__*/React.createElement("span", null, "Show numbers, a chart or a table from any part of your business.")), /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-btn ecc-btn-primary",
        onClick: this.openAdd
      }, "Add a panel"));
    }
    const patch = q => this.updatePanel(sel, q);
    const w = p.width || defaultWidth(p.kind);
    return /*#__PURE__*/React.createElement("aside", {
      className: "rb-side"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-side-head"
    }, /*#__PURE__*/React.createElement("b", null, p.title || autoTitle(this.state.registry, p)), /*#__PURE__*/React.createElement("span", null, "Change what this panel shows")), /*#__PURE__*/React.createElement("label", {
      className: "rb-field"
    }, "Panel name", /*#__PURE__*/React.createElement("input", {
      className: "rb-input",
      value: p.title || "",
      placeholder: "Name this panel",
      onChange: e => patch({
        title: e.target.value
      })
    })), /*#__PURE__*/React.createElement("div", {
      className: "rb-field"
    }, "Show as", this.kindSeg(p, patch)), this.numbersSection(p, patch, {
      allowBlend: true
    }), this.groupBy(p, patch), this.showTop(p, patch), this.filterChips(p, patch), /*#__PURE__*/React.createElement("div", {
      className: "rb-field"
    }, "Width", /*#__PURE__*/React.createElement("div", {
      className: "rb-seg two",
      role: "group",
      "aria-label": "Width"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: w === "half" ? "on" : "",
      onClick: () => patch({
        width: "half"
      })
    }, "Half"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: w === "full" ? "on" : "",
      onClick: () => patch({
        width: "full"
      })
    }, "Full"))), !this.chartMode() && /*#__PURE__*/React.createElement("label", {
      className: "rb-check rb-compare",
      title: "Applies to every panel in this report"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: comparison !== "none",
      onChange: e => this.setState({
        comparison: e.target.checked ? "previous_period" : "none"
      })
    }), /*#__PURE__*/React.createElement("span", null, "Compare with the period before", comparison === "previous_year" ? " (same period last year)" : "")), /*#__PURE__*/React.createElement("span", {
      className: "rb-grow"
    }), !this.chartMode() && /*#__PURE__*/React.createElement("div", {
      className: "rb-side-foot"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      disabled: sel === 0,
      onClick: () => this.movePanel(sel, -1)
    }, "Move up"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      disabled: sel === panels.length - 1,
      onClick: () => this.movePanel(sel, 1)
    }, "Move down"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      disabled: panels.length >= MAX_PANELS,
      onClick: () => this.duplicatePanel(sel)
    }, "Duplicate"), /*#__PURE__*/React.createElement("span", {
      className: "rb-grow"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link danger",
      onClick: () => this.removePanel(sel)
    }, "Remove")));
  }
  renderAdd() {
    const {
      adding,
      registry,
      range
    } = this.state;
    if (!adding) return null;
    const p = adding.draft;
    const patch = this.patchDraft;
    const ok = validPanel(registry, p);
    return /*#__PURE__*/React.createElement("div", {
      className: "rb-modal-back",
      onMouseDown: e => {
        if (e.target === e.currentTarget) this.setState({
          adding: null
        });
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-modal",
      role: "dialog",
      "aria-modal": "true",
      "aria-label": "Add a panel"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-modal-head"
    }, /*#__PURE__*/React.createElement("b", null, "Add a panel"), /*#__PURE__*/React.createElement("span", null, "1 What to show \xB7 2 Which numbers \xB7 3 How to show it"), /*#__PURE__*/React.createElement("span", {
      className: "rb-grow"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "rb-x",
      "aria-label": "Close",
      onClick: () => this.setState({
        adding: null
      })
    }, "\u2715")), /*#__PURE__*/React.createElement("div", {
      className: "rb-modal-body"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-col"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rb-col-h"
    }, "1 \xB7 What to show"), /*#__PURE__*/React.createElement("div", {
      className: "rb-areas"
    }, (registry.datasets || []).map(d => /*#__PURE__*/React.createElement("button", {
      type: "button",
      key: d.key,
      className: `rb-area${p.dataset === d.key ? " on" : ""}`,
      onClick: () => patch({
        dataset: d.key
      })
    }, /*#__PURE__*/React.createElement("b", null, areaLabel(registry, d.key)), /*#__PURE__*/React.createElement("span", null, areaBlurb(registry, d.key)))))), /*#__PURE__*/React.createElement("div", {
      className: "rb-col"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rb-col-h"
    }, "2 \xB7 Which numbers"), this.numberChecks(p, patch), this.groupBy(p, patch), this.showTop(p, patch)), /*#__PURE__*/React.createElement("div", {
      className: "rb-col"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rb-col-h"
    }, "3 \xB7 How to show it"), this.kindSeg(p, patch), /*#__PURE__*/React.createElement("span", {
      className: "rb-sub"
    }, "Preview, from your data for ", rangePhrase(range)), /*#__PURE__*/React.createElement("div", {
      className: "rb-card rb-preview"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-card-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "rb-card-title"
    }, p.title || autoTitle(this.state.registry, p))), this.renderBody(p)), /*#__PURE__*/React.createElement("label", {
      className: "rb-field"
    }, "Panel name", /*#__PURE__*/React.createElement("input", {
      className: "rb-input",
      value: p.title || "",
      placeholder: "Name this panel",
      onChange: e => patch({
        title: e.target.value
      })
    })))), /*#__PURE__*/React.createElement("div", {
      className: "rb-modal-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "rb-sub"
    }, "You can change any of this later from the panel."), /*#__PURE__*/React.createElement("span", {
      className: "rb-grow"
    }), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn",
      onClick: () => this.setState({
        adding: null
      })
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: !ok,
      title: ok ? "" : "Pick at least one number",
      onClick: this.commitAdd
    }, "Add to report"))));
  }

  // ---------------------------------------------------------------- page

  render() {
    const {
      registry,
      error,
      note,
      previewNote,
      name,
      shared,
      panels,
      saveBusy,
      range,
      previewBusy
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!registry) return /*#__PURE__*/React.createElement(Loader, null);
    const n = panels.length;
    const chart = this.chartMode();
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view ecc-view rb-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ecc-top rb-top"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-back",
      "aria-label": chart ? "Back to the Dashboard" : this.id() ? "Back to the report" : "Back to Reports",
      onClick: this.back
    }, /*#__PURE__*/React.createElement("svg", {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("path", {
      d: "M15 18l-6-6 6-6"
    }))), chart ? /*#__PURE__*/React.createElement("div", {
      className: "ecc-titleblock"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ecc-name",
      value: panels[0] && panels[0].title || "",
      placeholder: "Name this chart",
      "aria-label": "Chart name",
      onChange: e => {
        this.updatePanel(0, {
          title: e.target.value
        });
        this.setState({
          note: null
        });
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta"
    }, "New chart for your Dashboard \xB7 numbers below are live, from ", rangePhrase(range), previewBusy ? " · updating" : "")) : /*#__PURE__*/React.createElement("div", {
      className: "ecc-titleblock"
    }, /*#__PURE__*/React.createElement("input", {
      className: "ecc-name",
      ref: el => {
        this._nameRef = el;
      },
      value: name,
      placeholder: "Name this report",
      "aria-label": "Report name",
      onChange: e => this.setState({
        name: e.target.value,
        note: null
      })
    }), /*#__PURE__*/React.createElement("span", {
      className: "ecc-meta"
    }, n, " panel", n === 1 ? "" : "s", " \xB7 numbers below are live, from ", rangePhrase(range), previewBusy ? " · updating" : "")), /*#__PURE__*/React.createElement("span", {
      className: "ecc-spacer"
    }), chart ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "rb-chart-note"
    }, "Saved in your private \"Dashboard charts\" report"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn",
      disabled: saveBusy,
      onClick: this.back
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: saveBusy || !n,
      onClick: this.addToDashboard
    }, saveBusy ? "Adding…" : "Add to Dashboard")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
      className: "rb-who"
    }, "Who can see it", /*#__PURE__*/React.createElement("select", {
      className: "rb-select",
      value: shared ? "team" : "me",
      onChange: e => this.setState({
        shared: e.target.value === "team"
      })
    }, /*#__PURE__*/React.createElement("option", {
      value: "team"
    }, "Your team"), /*#__PURE__*/React.createElement("option", {
      value: "me"
    }, "Just you"))), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn",
      disabled: saveBusy || !shared,
      title: shared ? "Save, then choose who gets it by email and how often" : "Reports only you can see can't be emailed. Switch to Your team first.",
      onClick: () => this.save(true)
    }, "Save and email it"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-btn ecc-btn-primary",
      disabled: saveBusy,
      onClick: () => this.save()
    }, saveBusy ? "Saving…" : "Save"))), note ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice rb-banner"
    }, note) : null, previewNote ? /*#__PURE__*/React.createElement("div", {
      className: "wa-notice rb-banner"
    }, previewNote, " ", /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "ecc-link",
      onClick: this.retryPreview
    }, "Try again")) : null, /*#__PURE__*/React.createElement("div", {
      className: "rb-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "rb-canvas"
    }, panels.map((p, i) => this.renderCard(p, i)), chart ? null : n < MAX_PANELS ? /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: `rb-add${n === 0 ? " big" : ""}`,
      onClick: this.openAdd
    }, /*#__PURE__*/React.createElement("b", null, "+ Add a panel"), /*#__PURE__*/React.createElement("span", null, "Numbers, a chart or a table")) : /*#__PURE__*/React.createElement("p", {
      className: "rb-hint rb-full"
    }, "A report holds up to ", MAX_PANELS, " panels. Remove one to add another.")), this.renderSide()), this.renderAdd());
  }
}
export const ReportBuilderViewComponent = withRouter(ReportBuilderComponent);
export default {
  ReportBuilderViewComponent
};